// Picker route. Owns the top-level switch between selector (no active
// wave) and the live scan screen (active wave). Server is authoritative
// for "which picker is on which wave" — there is no client-side
// localStorage of in-progress state. Reload / re-login resumes the
// wave from the server.
import { useCallback } from "react";
import FlowLoader from "../components/FlowLoader";
import PickTypeSelector from "../components/picking/PickTypeSelector";
import ActiveWaveScreen from "../components/picking/ActiveWaveScreen";
import { useActiveWave } from "../hooks/useActiveWave";
import "../styles/picking.css";
// The missing-item count screen mirrors the Stock Check module's row list
// (same `stock-check-*` classes), so its stylesheet must load here too.
import "../styles/stock-check.css";

const Picking = () => {
    const { data, loading, refetch } = useActiveWave();

    const handleWaveStarted = useCallback(() => { refetch(); }, [refetch]);
    const handleWaveComplete = useCallback(() => { refetch(); }, [refetch]);

    if (loading && !data) {
        // Loader-only — the FlowLoader animation is feedback enough;
        // suppress the "Loading…" caption so the page reads cleaner
        // on slow first paints.
        return (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
                <FlowLoader />
            </div>
        );
    }

    if (data?.wave && data.progress) {
        // Hydrate the lookahead queue from `lookahead` when the BE
        // includes it (new shape). Fall back to single-element queue
        // built from `nextLine` so an older bundle paired with the
        // updated FE still works during a partial rollout.
        const initialLookahead = Array.isArray(data.lookahead)
            ? data.lookahead
            : (data.nextLine ? [data.nextLine] : []);
        return (
            <ActiveWaveScreen
                // Keyed by wave so a Large wave→wave transition (the
                // next-order prompt starts a NEW wave while this screen
                // is still mounted) remounts with clean state instead of
                // carrying the finished wave's phase machine over.
                key={data.wave.wave_id}
                wave={data.wave}
                initialCurrentLine={data.currentLine}
                initialLookahead={initialLookahead}
                initialProgress={data.progress}
                onWaveComplete={handleWaveComplete}
                initialLines={data.lines}
                initialBarcodeMap={data.barcodeMap}
            />
        );
    }

    return <PickTypeSelector onWaveStarted={handleWaveStarted} />;
};

export default Picking;
